import { ChaveIndicadorPerformance, IndicadorPerformance, PontoSerieDiaria } from './_models'

/** Ordem de exibição dos cartões e gráficos — a do contrato, nunca `Object.keys` do payload. */
export const ORDEM_INDICADORES_PERFORMANCE: ChaveIndicadorPerformance[] = [
  'tempoMedioEspera',
  'tempoMedioTriagem',
  'tempoMedioConsulta',
  'taxaCancelamento',
  'taxaAbsenteismo',
  'taxaResolutividade',
  'slaCumprido',
]

export type SentidoMelhora = 'queda' | 'alta' | 'neutro'

/**
 * Em qual direção o indicador melhora. É apresentação: o contrato não traz esse campo.
 * Consulta é neutra: consulta mais longa não é, por si, melhor nem pior.
 */
export const SENTIDO_MELHORA: Record<ChaveIndicadorPerformance, SentidoMelhora> = {
  tempoMedioEspera: 'queda',
  tempoMedioTriagem: 'queda',
  tempoMedioConsulta: 'neutro',
  taxaCancelamento: 'queda',
  taxaAbsenteismo: 'queda',
  taxaResolutividade: 'alta',
  slaCumprido: 'alta',
}

export type AvaliacaoVariacao = 'melhora' | 'piora' | 'neutra' | 'estavel' | 'sem_base'

export const avaliarVariacao = (
  chave: ChaveIndicadorPerformance,
  variacao: number | null
): AvaliacaoVariacao => {
  if (variacao === null || variacao === undefined || Number.isNaN(variacao)) return 'sem_base'
  if (variacao === 0) return 'estavel'

  const sentido = SENTIDO_MELHORA[chave]
  if (sentido === 'neutro') return 'neutra'

  const subiu = variacao > 0
  return subiu === (sentido === 'alta') ? 'melhora' : 'piora'
}

/** Prefixo das chaves de i18n de cada indicador (`<prefixo>.nome`, `<prefixo>.denominador`). */
export const CHAVE_I18N_INDICADOR: Record<ChaveIndicadorPerformance, string> = {
  tempoMedioEspera: 'indicadores.performance.tempo_espera',
  tempoMedioTriagem: 'indicadores.performance.tempo_triagem',
  tempoMedioConsulta: 'indicadores.performance.tempo_consulta',
  taxaCancelamento: 'indicadores.performance.taxa_cancelamento',
  taxaAbsenteismo: 'indicadores.performance.taxa_absenteismo',
  taxaResolutividade: 'indicadores.performance.taxa_resolutividade',
  slaCumprido: 'indicadores.performance.sla_cumprido',
}

export const ICONE_INDICADOR: Record<ChaveIndicadorPerformance, string> = {
  tempoMedioEspera: 'bi-clock',
  tempoMedioTriagem: 'bi-heart-pulse',
  tempoMedioConsulta: 'bi-camera-video',
  taxaCancelamento: 'bi-calendar-x',
  taxaAbsenteismo: 'bi-person-x',
  taxaResolutividade: 'bi-check2-circle',
  slaCumprido: 'bi-stopwatch',
}

/** Contagem inteira de um dia de taxa (`valor·n/100`); `null` sem dado. */
export const contagemDoDia = (ponto: PontoSerieDiaria): number | null =>
  ponto.valor === null || ponto.n <= 0 ? null : Math.round((ponto.valor * ponto.n) / 100)

// `valor` vem em escala 1, HALF_UP: X confere quando X·100/n cai a até meio décimo dele.
const confere = (x: number, n: number, valor: number) =>
  Math.abs((x * 100) / n - valor) <= 0.05 + 1e-9

/**
 * Numerador (X) de uma taxa, para o "X de Y" do cartão. Soma as contagens diárias (exatas
 * para n diário < 1000) e, se não fecharem com o período, tenta `valor·n/100`. Quando
 * nenhuma confere, devolve `null` e o cartão fica com o texto do denominador.
 */
export const contagemDerivada = (indicador: IndicadorPerformance): number | null => {
  const { unidade, valor, n, serie } = indicador
  if (unidade !== 'PERCENTUAL' || valor === null || n <= 0) return null

  if (serie) {
    const somaN = serie.reduce((soma, ponto) => soma + ponto.n, 0)
    const somaX = serie.reduce((soma, ponto) => soma + (contagemDoDia(ponto) ?? 0), 0)
    if (somaN === n && confere(somaX, n, valor)) return somaX
  }

  const estimada = Math.round((valor * n) / 100)
  return confere(estimada, n, valor) ? estimada : null
}
